"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSection = createSection;
exports.createSectionContent = createSectionContent;
exports.createList = createList;
exports.createMixedList = createMixedList;
exports.createNumberListItem = createNumberListItem;
exports.createStringListItem = createStringListItem;
exports.createFormattedMessageListItem = createFormattedMessageListItem;
exports.createGroupList = createGroupList;
exports.createGroup = createGroup;
exports.createGroupContent = createGroupContent;
exports.createFormattedMessageRecord = createFormattedMessageRecord;
exports.createFormattedMessageValueRecord = createFormattedMessageValueRecord;
exports.createFormattedMessageValueRecordFromEvent = createFormattedMessageValueRecordFromEvent;
exports.createNumberRecord = createNumberRecord;
exports.createNumberRecordFromEvent = createNumberRecordFromEvent;
exports.createNumberValueRecord = createNumberValueRecord;
exports.createNumberValueRecordFromEvent = createNumberValueRecordFromEvent;
exports.createStringRecord = createStringRecord;
exports.compareByTimestamp = compareByTimestamp;
exports.compareByTimestampReverse = compareByTimestampReverse;
const ramda_1 = require("ramda");
const events_1 = require("../../events");
const functional_1 = require("../../utils/functional");
const common_1 = require("../../utils/common");
const attachments_1 = require("../../attachments");
function createSection({ formatMessage }, { label, content }) {
    const attachments = (0, attachments_1.uniqueAttachmentList)(content
        .flatMap(item => {
        if (item.type === "group-list") {
            return item.groups.flatMap(item => item.content);
        }
        if (item.type === "group") {
            return item.content;
        }
        return item;
    })
        .flatMap(item => item.attachments || []));
    return {
        type: "section",
        label: formatMessage(label),
        attachments,
        content
    };
}
function createSectionContent(content) {
    return content.filter(common_1.isDefined);
}
function createList(intl, { events, label, values, createItem }) {
    const { formatMessage } = intl;
    const labels = values.map(({ label }) => label);
    const queries = values.map(({ query }) => query);
    const relevantEvents = (0, events_1.findEventsByQueries)(events, queries);
    const attachments = (0, events_1.mapEventsToAttachments)(relevantEvents.filter(common_1.isDefined));
    const items = (0, ramda_1.zip)(labels, relevantEvents)
        .filter(([, event]) => event !== undefined)
        .map(([label, event]) => createItem(intl, { label, event: event }));
    if (items.length === 0) {
        return undefined;
    }
    return {
        type: "list",
        label: formatMessage(label),
        attachments,
        items
    };
}
function createMixedList(intl, { events, label, values }) {
    const { formatMessage } = intl;
    const queries = values.map(({ query }) => query);
    const relevantEvents = (0, events_1.findEventsByQueries)(events, queries);
    const attachments = (0, events_1.mapEventsToAttachments)(relevantEvents.filter(common_1.isDefined));
    const items = (0, ramda_1.zip)(values, relevantEvents)
        .filter(([, event]) => event !== undefined)
        .map(([value, event]) => {
        if (value.type === "number") {
            return createNumberListItem(intl, {
                label: value.label,
                event: event
            });
        }
        if (value.type === "string") {
            return createStringListItem(intl, {
                label: value.label,
                event: event
            });
        }
        return createFormattedMessageListItem(intl, {
            label: value.label,
            event: event,
            messages: value.messages
        });
    });
    if (items.length === 0) {
        return undefined;
    }
    return {
        type: "list",
        label: formatMessage(label),
        attachments,
        items
    };
}
function createNumberListItem({ formatMessage }, { label, event }) {
    const { value, schema: { unit } = {}, timestamp, sid, origin } = event;
    return {
        type: "list-item",
        label: formatMessage(label),
        value: (0, common_1.appendUnit)(value, unit),
        timestamp,
        sid,
        origin
    };
}
function createStringListItem({ formatMessage }, { label, event }) {
    const { value, timestamp, sid, origin } = event;
    return {
        type: "list-item",
        label: formatMessage(label),
        value,
        timestamp,
        sid,
        origin
    };
}
function createFormattedMessageListItem({ formatMessage, formatMessageMap }, { label, event, messages }) {
    const { value, timestamp, sid, origin } = event;
    return {
        type: "list-item",
        label: formatMessage(label),
        value: formatMessageMap(messages, value),
        timestamp,
        sid,
        origin
    };
}
function createGroupList({ formatMessage }, { events, groups, values, formatValue }) {
    const predicates = groups.map(({ predicate }) => predicate);
    const queries = values.map(({ query }) => query);
    const relevantEvents = (0, events_1.findEventsByQueries)(events, queries);
    const tempGroups = (0, ramda_1.zip)(groups, (0, functional_1.partitionWith)(values.map(({ label, query }, i) => ({
        label,
        query,
        event: relevantEvents[i]
    })), predicates.map(fn => (0, ramda_1.compose)(fn, ({ event }) => event))));
    const finalGroups = tempGroups
        .map(([{ variant, label }, content]) => ({
        type: "group",
        variant,
        label: formatMessage(label),
        content: content.map(({ label, query, event }) => {
            if (event === undefined) {
                let sid = "";
                if (query.interpretation !== undefined) {
                    sid = (0, events_1.mapInterpretationToString)(query.interpretation);
                }
                return {
                    type: "record",
                    variant: "short",
                    label: formatMessage(label),
                    sid
                };
            }
            const { value, timestamp, sid, origin } = event;
            if (formatValue === undefined) {
                return {
                    type: "record",
                    variant: "short",
                    label: formatMessage(label),
                    attachments: (0, events_1.mapEventsToAttachments)(event),
                    timestamp,
                    sid,
                    origin
                };
            }
            return {
                type: "record",
                variant: "short",
                label: formatMessage(label),
                attachments: (0, events_1.mapEventsToAttachments)(event),
                value: formatValue(value),
                timestamp,
                sid,
                origin
            };
        })
    }))
        .filter(group => group.content.length !== 0);
    if (finalGroups.length === 0 ||
        finalGroups.every(group => group.content.length === 0)) {
        return undefined;
    }
    return { type: "group-list", groups: finalGroups };
}
function createGroup({ formatMessage }, { variant, label, content: rawContent }) {
    const content = rawContent.filter(common_1.isDefined);
    if (content.length === 0) {
        return undefined;
    }
    return {
        type: "group",
        variant,
        label: formatMessage(label),
        content
    };
}
function createGroupContent(content) {
    return content.filter(common_1.isDefined);
}
function createFormattedMessageRecord({ formatMessage, formatMessageMap }, { events, variant, label, query, messages }) {
    const event = (0, events_1.findLastEventByQuery)(events, query);
    if (event === undefined) {
        return undefined;
    }
    const { value, timestamp, sid, origin } = event;
    return {
        type: "record",
        variant,
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: formatMessageMap(messages, value),
        timestamp,
        sid,
        origin
    };
}
function createFormattedMessageValueRecord({ formatMessage, formatMessageMap }, { events, variant, label, query, messages }) {
    const event = (0, events_1.findLastEventByQuery)(events, query);
    if (event === undefined) {
        return undefined;
    }
    const { value, timestamp, sid, origin } = event;
    return {
        type: "record",
        variant,
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: formatMessageMap(messages, value.result),
        timestamp,
        sid,
        origin
    };
}
function createFormattedMessageValueRecordFromEvent({ formatMessage, formatMessageMap }, { variant, label, event, messages }) {
    const { value, timestamp, sid, origin } = event;
    return {
        type: "record",
        variant,
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: formatMessageMap(messages, value.result),
        timestamp,
        sid,
        origin
    };
}
function createNumberRecord({ formatMessage }, { events, label, query }) {
    const event = (0, events_1.findLastEventByQuery)(events, query);
    if (event === undefined) {
        return undefined;
    }
    const { value, schema: { unit } = {}, timestamp, sid, origin } = event;
    return {
        type: "record",
        variant: "short",
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: (0, common_1.appendUnit)(value, unit),
        timestamp,
        sid,
        origin
    };
}
function createNumberRecordFromEvent({ formatMessage }, { variant, label, event }) {
    const { value, schema: { unit } = {}, timestamp, sid, origin } = event;
    return {
        type: "record",
        variant,
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: (0, common_1.appendUnit)(value, unit),
        timestamp,
        sid,
        origin
    };
}
function createNumberValueRecord({ formatMessage }, { events, label, query }) {
    const event = (0, events_1.findLastEventByQuery)(events, query);
    if (event === undefined) {
        return undefined;
    }
    const { value, schema: { unit } = {} } = event;
    return {
        type: "record",
        variant: "short",
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: (0, common_1.appendUnit)(value.value, unit),
        timestamp: event.timestamp,
        origin: event.origin
    };
}
function createNumberValueRecordFromEvent({ formatMessage }, { variant, label, event }) {
    const { value, schema: { unit } = {} } = event;
    return {
        type: "record",
        variant,
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value: (0, common_1.appendUnit)(value.value, unit),
        timestamp: event.timestamp,
        origin: event.origin
    };
}
function createStringRecord({ formatMessage }, { events, variant, label, query }) {
    const event = (0, events_1.findLastEventByQuery)(events, query);
    if (event === undefined) {
        return undefined;
    }
    const { value, timestamp, sid, origin } = event;
    return {
        type: "record",
        variant,
        label: formatMessage(label),
        attachments: (0, events_1.mapEventsToAttachments)(event),
        value,
        timestamp,
        sid,
        origin
    };
}
function compareByTimestamp(a, b) {
    const dA = new Date(a.timestamp);
    const dB = new Date(b.timestamp);
    if (dA > dB) {
        return -1;
    }
    if (dA < dB) {
        return 1;
    }
    return 0;
}
function compareByTimestampReverse(a, b) {
    return -compareByTimestamp(a, b);
}
